import { motion } from "framer-motion";
import { Check, X, Bot, ThumbsDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useCompleteItem, useDismissItem, useDismissNotImportant } from "@/hooks/useItems";
import { useAwardXP } from "@/hooks/useUserStats";
import { useItemJob } from "@/hooks/useAgentJobs";
import { playXpGain, playLevelUp, playDailyTarget } from "@/lib/sounds";
import { emitXpPopup } from "@/components/ui/xp-popup";
import type { Item } from "@/lib/types";

const priorityColors = {
  high: "bg-accent",
  medium: "bg-secondary",
  low: "bg-muted-foreground/50",
};

function getAgeBadge(createdAt: string) {
  const days = Math.floor(
    (Date.now() - new Date(createdAt).getTime()) / 86400000
  );
  if (days < 1) return null;
  const label = days >= 14 ? "2w+" : days >= 7 ? "1w" : `${days}d`;
  const color =
    days >= 7
      ? "bg-red-500/10 text-red-500 border-red-500/20"
      : days >= 3
      ? "bg-amber-500/10 text-amber-500 border-amber-500/20"
      : "bg-muted text-muted-foreground border-border";
  return { label, color };
}

export function ItemCard({
  item,
  onClick,
  onDispatch,
  subtaskCount,
  compact,
  projectColor,
  hideTag,
}: {
  item: Item;
  onClick?: () => void;
  onDispatch?: (item: Item) => void;
  subtaskCount?: { done: number; total: number };
  compact?: boolean;
  projectColor?: string;
  hideTag?: boolean;
}) {
  const completeItem = useCompleteItem();
  const dismissItem = useDismissItem();
  const dismissNotImportant = useDismissNotImportant();
  const awardXP = useAwardXP();
  const { data: activeJob } = useItemJob(item.id);

  const jobStatus = activeJob?.status;
  const isAgentManaged = jobStatus === "completed" || jobStatus === "failed";
  const age = getAgeBadge(item.created_at);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 100, transition: { duration: 0.2 } }}
      className={`flex items-center gap-3 rounded-lg bg-card border border-border hover:border-l-[3px] hover:border-l-primary hover:border-primary/30 transition-colors group ${compact ? "p-2" : "p-3"} ${item.priority === "high" && !compact ? "ring-1 ring-accent/30" : ""}`}
    >
      {/* Priority dot */}
      <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${priorityColors[item.priority]}`} />

      {/* Content - clickable for edit */}
      <div className="flex-1 min-w-0 cursor-pointer" onClick={onClick}>
        <div className="flex items-start gap-2">
          <span className={`font-medium line-clamp-2 ${compact ? "text-xs" : "text-sm"}`}>
            {item.title}
          </span>
          {item.project_tag && !hideTag && projectColor && (
            <div
              className="w-2 h-2 rounded-full shrink-0 mt-1.5"
              style={{ backgroundColor: projectColor }}
              title={item.project_tag}
            />
          )}
          {age && (
            <Badge variant="outline" className={`text-[10px] shrink-0 mt-0.5 px-1.5 py-0 ${age.color}`}>
              {age.label}
            </Badge>
          )}
          {subtaskCount && subtaskCount.total > 0 && (
            <div className="flex items-center gap-1 shrink-0 mt-0.5">
              <div className="w-12 h-1.5 rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full rounded-full bg-primary transition-all"
                  style={{ width: `${(subtaskCount.done / subtaskCount.total) * 100}%` }}
                />
              </div>
              <span className="text-[10px] text-muted-foreground">
                {subtaskCount.done}/{subtaskCount.total}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Agent status: dispatch button or job badge */}
      {item.automatable && !activeJob && (
        <button
          onClick={() => onDispatch?.(item)}
          className="shrink-0 text-muted-foreground hover:text-primary transition-colors"
          title="Dispatch to agent"
        >
          <Bot className="h-3.5 w-3.5" />
        </button>
      )}

      {activeJob && (jobStatus === "pending" || jobStatus === "claimed") && (
        <Badge variant="outline" className="shrink-0 text-xs border-yellow-500 text-yellow-500">
          Queued
        </Badge>
      )}

      {activeJob && jobStatus === "running" && (
        <Badge variant="outline" className="shrink-0 text-xs border-blue-500 text-blue-500 animate-pulse">
          Working
        </Badge>
      )}

      {activeJob && jobStatus === "completed" && (
        activeJob.pr_url ? (
          <a href={activeJob.pr_url} target="_blank" rel="noopener noreferrer">
            <Badge variant="outline" className="shrink-0 text-xs border-green-500 text-green-500 hover:bg-green-500/10 cursor-pointer">
              Review
            </Badge>
          </a>
        ) : (
          <Badge variant="outline" className="shrink-0 text-xs border-green-500 text-green-500">
            Review
          </Badge>
        )
      )}

      {activeJob && jobStatus === "failed" && (
        <Badge variant="outline" className="shrink-0 text-xs border-red-500 text-red-500">
          Failed
        </Badge>
      )}

      {/* Actions - always visible on mobile, hover-only on desktop */}
      {!isAgentManaged && (
        <div className="flex items-center gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 text-primary hover:text-primary"
            onClick={(e: React.MouseEvent) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const x = rect.left;
              const y = rect.top;
              completeItem.mutate(
                { id: item.id, xpValue: item.xp_value, priority: item.priority },
                {
                  onSuccess: () => {
                    awardXP.mutateAsync({
                      itemXpValue: item.xp_value,
                      itemPriority: item.priority,
                    }).then((result) => {
                      emitXpPopup(result.xpGained, x, y);
                      playXpGain(result.xpGained);
                      if (result.leveledUp) playLevelUp();
                      if (result.dailyTargetHit) playDailyTarget();
                    });
                  },
                }
              );
            }}
          >
            <Check className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 text-muted-foreground hover:text-amber-500"
            onClick={() => dismissNotImportant.mutate(item.id)}
            title="Not important — teach the AI"
          >
            <ThumbsDown className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 text-muted-foreground hover:text-destructive"
            onClick={() => dismissItem.mutate(item.id)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}
    </motion.div>
  );
}
